import { queryOptions } from '@tanstack/react-query';

import {
  apiGet,
  apiPatch,
  apiPost,
  type ApiError,
  type BodyOf,
  type QueryOf,
  type ResponseOf,
} from './http';
import { queryKeys } from './queryKeys';

/**
 * The staff directory — `GET /api/v1/users/`, the route the API did not have until backend TASK-090.
 *
 * ⚠️ **Why it exists at all, because it changes what the product can do rather than how it looks.**
 * `InterviewIn.interviewer_id` is a user id, and with nothing listing users the only id this client
 * could honestly send was the caller's own — so §6.5's dialog could schedule the caller and nobody
 * else, and an account in the `Interviewer` group, whose entire reach is `Interview.interviewer`,
 * could be given no work at all through the interface. `ScheduleInterviewDialog`'s docblock carried
 * that as a **sentence** for one sprint; this module is what replaces it.
 *
 * ⚠️ **`Admin` and `Recruiter` only — an `Interviewer` is 403** (ADR 0016;
 * `Route("get", USERS, None, (401, 403, 200, 200, 403, 403))` in
 * `backend/tests/test_permission_matrix.py`). The **matrix row is the evidence**; the client gate is
 * what stops a request nobody is entitled to make, and `mayReadStaffDirectory` below is where it lives
 * so no screen re-derives it.
 *
 * **What a row says**: the id (which is what a schedule body takes), the display name, the email — the
 * name may *be* an email, so both are needed to tell two people apart — and the group names, because
 * an account in **no** group can be assigned an interview and then cannot open it.
 *
 * ⚠️ **Two more facts arrived with backend TASK-115, and both exist for §6.10 rather than for the
 * picker**: `is_active`, which is what makes a retired row legible as retired once `?include_retired=`
 * is asked for, and `has_password`, which says whether the account has a password **at all** — the
 * only way an Admin about to press *Reactivate* can tell somebody who comes back able to sign in from
 * somebody who never claimed their account. `invitation_pending` cannot answer that: an **expired**
 * invitation leaves it false on an account with no password, which is precisely the case §6.10 has to
 * explain. ADR 0016 records both as disclosure decisions, because a `Recruiter` reads this payload
 * too and there is no per-reader shape.
 */

/**
 * `?include_retired=`, `?page=` and `?page_size=`, read off the schema rather than restated.
 *
 * ⚠️ **`include_retired` is an opt-in and every caller that does not want retired rows passes
 * nothing** (backend TASK-115). The default answer is unchanged, which is what keeps §6.5's picker
 * safe: `get_interviewer` is untouched, so an account this parameter admits is still a 404 to
 * schedule against, and a list that offered it would be offering a name that cannot be assigned. It
 * is answered to an `Admin` only, and a `Recruiter` who passes it is answered **as though they had
 * not** — not a 403, which would itself confirm that a retired colleague exists. §6.10 is Admin-only
 * (`maySeeSettings`), so the one screen that sends it cannot be read by anybody it is ignored for.
 *
 * ⚠️ **`?search=` used to be here and is gone** (BUG-048, decided by the user 2026-08-05): the route
 * took it, this file documented it twice, and **both** call sites passed nothing — a searchable staff
 * directory that nothing searched. It was removed on this codebase's own rule against shipping ahead
 * of a caller, and it comes back with the screen that needs it, in that screen's commit. Nothing here
 * had to be edited for the key to go: the type derives from the OpenAPI document.
 */
export type StaffQuery = NonNullable<QueryOf<'/api/v1/users/', 'get'>>;

type StaffPageFromApi = ResponseOf<'/api/v1/users/', 'get'>;

/** `StaffOut` — one account of the caller's own tenant. */
export type Staff = StaffPageFromApi['items'][number];

export type StaffPage = StaffPageFromApi;

export async function fetchStaff(query: StaffQuery = {}): Promise<StaffPage> {
  return apiGet('/api/v1/users/', { query });
}

/**
 * ⚠️ **The query is in the key, and that is Sprint 10's `roles` finding applied before a screen can
 * repeat it.** Page two sharing one cache entry with page one would serve one page's rows as *the*
 * directory to the next reader — `queryKeys.staff` carries the query for exactly that reason, and
 * `api.test.ts` asserts it. ⚠️ **The reason is `page`, not `search`**: this argument was written about
 * a `?search=` that no screen ever sent and that BUG-048 removed, and it holds unchanged without it —
 * `?page=` and `?page_size=` are in the document today and each narrows the answer.
 *
 * **No viewer segment, and the argument is `queryKeys.roles`'s rather than a weaker copy of it.** This
 * route is **refused** per caller (an `Interviewer` is 403) and never *narrowed*: every caller who is
 * answered at all is answered their tenant's whole staff, so no viewer's list can be served to
 * another. What a viewer segment would not close either is the hazard that rule names — an entry
 * outliving the permission that filled it, which happens through a demotion **without** a re-login and
 * therefore with an unchanged user id. That is closed at the **read**: the caller gates on the group as
 * well as on `enabled`, and `scheduleInterview.test.tsx` asserts the rows are not *fetched* rather than
 * only that they are not rendered.
 */
export function staffQuery(query: StaffQuery = {}) {
  return queryOptions<StaffPage, ApiError>({
    queryKey: queryKeys.staff(query),
    queryFn: () => fetchStaff(query),
  });
}

// --- The writes (backend TASK-099, TASK-100) ----------------------------------------------------
//
// ⚠️ **`Admin` only — strictly narrower than the read above, and the pair is the point.** A Recruiter
// reads the directory because assignment needs it and may change nothing in it:
// `STAFF_WRITE_ACCESS` is a *separate* named dependency from `STAFF_READ_ACCESS` on the server, so
// widening who may see the list cannot widen who may create accounts. That split is pinned by the
// permission matrix rather than by a client predicate — see the note at the foot of this file for why
// there is deliberately no `mayWriteStaff` client half (BUG-040).

export type NewStaff = BodyOf<'/api/v1/users/', 'post'>;

/** `StaffCreated` — the account **and** its first invitation, in one response. */
export type CreatedStaff = ResponseOf<'/api/v1/users/', 'post'>;

/** `InvitationOut` — a one-time link. ⚠️ The only payload in the product that carries a token. */
export type Invitation = ResponseOf<'/api/v1/users/{user_id}/invitation/', 'post'>;

/**
 * Add a colleague. ⚠️ **There is no password field and there never will be**: the account is created
 * with an unusable one and the response carries a one-time link the Admin hands over, so no
 * administrator ever knows a colleague's password — in a product holding candidates' personal data,
 * somebody who does can sign in as them and read feedback as them.
 *
 * ⚠️ **Two guards hold that, not one** (BUG-027). This route's `set_unusable_password` was the only
 * one for a release, and it never covered `reissueInvitation` below — which issued a password-setting
 * link for *any* account in the tenant, an active colleague included. `issue_invitation` refuses that
 * now, so the sentence above holds on both routes rather than only on this one — **for an account
 * whose owner has claimed it.** Until somebody claims it, the Admin who created it is holding its
 * one-time link, and redemption asks nothing about who redeems; that is what a copied link costs
 * where there is no mail, and no guard on either route can close it.
 *
 * ⚠️ **The invitation is in the create response because it is one operation.** Two requests would
 * leave a window in which a screen failing between them had produced a person who cannot sign in and
 * an Admin with no link to hand over.
 */
export const createStaff = (staff: NewStaff) => apiPost('/api/v1/users/', staff);

/**
 * Move somebody to exactly one group — the model admits several, the product does not.
 *
 * ⚠️ **A `422` here is the workspace refusing to lock itself out**, not a form error: the last active
 * `Admin` may not be demoted, and an Admin with colleagues may not demote themselves. The body's
 * `detail` is the sentence to render — it names the action that unblocks the reader ("give somebody
 * else the Admin group first"), which no client-side copy could.
 */
export const setStaffGroup = (userId: number, group: string) =>
  apiPatch('/api/v1/users/{user_id}/', { group }, { params: { user_id: userId } });

/**
 * Retire an account. **Not deletion**: their conducted interviews keep them as author, their
 * submitted feedback still carries their name, and they leave every picker. Their live session stops
 * working on their next request.
 */
export const deactivateStaff = (userId: number) =>
  apiPost('/api/v1/users/{user_id}/deactivate/', undefined, { params: { user_id: userId } });

/**
 * Bring a retired account back — `is_active = True`, and **nothing else** (backend TASK-114).
 *
 * ⚠️ **What it deliberately does not do is the part a screen has to say out loud.** The invitation
 * the retirement revoked stays revoked, permanently (BUG-044: retiring is how an Admin answers *"the
 * link went to the wrong person"*, and a restore that resurrected the token would undo exactly that).
 * So an account that never redeemed one comes back **unable to sign in**, and the way in is a fresh
 * link — one more deliberate act. `StaffOut.has_password` is the fact §6.10 renders that sentence
 * from; nothing here computes it.
 *
 * ⚠️ **There is no `422` on this route and no client-side refusal to build for one.** Neither of
 * `deactivateStaff`'s two refusals reverses: the last-Admin rule can only be moved *up* by this call,
 * and the caller's own account is necessarily active (their session resolved), so
 * `POST …/{own id}/reactivate/` is a 200 no-op. The document declares `200` and `404` and nothing
 * else — a `404` is another tenant's id or one that never existed, byte-identical (ADR 0013).
 */
export const reactivateStaff = (userId: number) =>
  apiPost('/api/v1/users/{user_id}/reactivate/', undefined, { params: { user_id: userId } });

/**
 * Issue a **new** link for somebody who lost theirs — and invalidate the old one in the same
 * statement. ⚠️ This is the only way to see a link again: the token is stored hashed, so nothing can
 * reproduce the previous one.
 *
 * ⚠️ **A `422` here is the account refusing to be taken over** (BUG-027), not a form error: an
 * invitation *sets* a password, so one for somebody who has already chosen theirs would let an Admin
 * pick it and sign in as them. The `detail` is the sentence to render — it names the alternative
 * (retire the account) and states that this product has no password reset, which no client-side copy
 * could — and `UsersSection` shows it in a dialog.
 *
 * ⚠️ **The guard is never predicted from a row.** `invitation_pending` is *false* for an **expired**
 * invitation on an account whose password is still unusable, which is the case this route exists for,
 * so a client deriving the outcome from it would hide the control on the primary use case. The one
 * exception is the reader's own row, where a usable password is a certainty.
 */
export const reissueInvitation = (userId: number) =>
  apiPost('/api/v1/users/{user_id}/invitation/', undefined, { params: { user_id: userId } });

/*
 * ⚠️ **There is deliberately no `mayWriteStaff` here** (BUG-040, 2026-08-03). One existed, exported,
 * with zero callers and a body identical to `pages/Settings/settingsAccess.ts`'s `maySeeSettings` —
 * one rule in two copies, the dead one shipped in the very sprint whose TASK-097 exists to stop that.
 *
 * It has no caller because it can have none that means anything: **every** staff write lives inside
 * §6.10, and the whole screen is already gated on `maySeeSettings`, so a second Admin check on each
 * control would be a predicate that cannot be false where it is evaluated. Deleted rather than wired
 * up, because wiring it would have added the redundancy instead of removing it.
 *
 * ⚠️ **What that does *not* collapse** — and this is the part worth keeping from the old docstring —
 * is the server's read/write split. `STAFF_WRITE_ACCESS` is a *separate* named dependency from
 * `STAFF_READ_ACCESS`, so widening who may read the directory cannot widen who may create accounts,
 * and `mayReadStaffDirectory` above stays a distinct predicate for that reason: a Recruiter is 200 on
 * the `GET` and 403 on all four writes. The permission matrix rows are the evidence for that, not a
 * client-side predicate — which is the right place for it, since a client gate only ever decides what
 * is **rendered**.
 */
